/**
 * CallOverlay — full-screen overlay rendered ONLY when a call is in
 * progress (ringing / accepted). Three states share a single shell:
 *
 *   • outgoing + ringing  →  "Calling …" + Cancel button + outgoing ringtone
 *   • incoming + ringing  →  Avatar + Accept/Decline + incoming ringtone + vibrate
 *   • accepted (video)    →  Contained remote video + local preview corner
 *   • accepted (voice)    →  Peer card + hidden <audio> for remote audio
 *
 * The overlay is positioned `fixed inset-0 z-[100]` so it sits above
 * the Messenger UI, the mobile-chat-active body class, and any modals.
 * On mobile it consumes the full viewport (100dvh); on desktop it
 * pops as a centred card with the message thread visible behind a
 * scrim.
 *
 * Iter155 fixes (Wayne smoke):
 *   • CALLER race: when WebRTC media (`ontrack` → setRemoteStream)
 *     arrives BEFORE the `call_accepted` signal is processed, the
 *     remote `<video>` wasn't mounted yet, so the legacy
 *     `useEffect([remoteStream])` attached srcObject to a null ref.
 *     The element later mounted but the effect didn't re-run. Fix:
 *     callback refs attach the stream EVERY time the element mounts
 *     OR the stream changes — race-proof.
 *   • Voice calls: there was NO remote audio element at all (the
 *     <video> only renders for video calls). Voice now mounts a
 *     hidden <audio autoPlay> for the remote stream.
 *   • Autoplay block on mobile: if `.play()` rejects (iOS Safari
 *     gating without user gesture), a "Tap to enable sound" button
 *     surfaces. Clicking it inside a user gesture retries play().
 *   • Ringtone: WebAudio-synthesized ringtone via useRingtone hook
 *     (gentle outgoing tone, urgent incoming tone, Vibration API on
 *     supported devices).
 */
import { createPortal } from 'react-dom';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Phone, PhoneOff, Video, VideoOff, Mic, MicOff, Volume2, MoreHorizontal, Maximize2, Circle, Square, MessageSquare, X, GripHorizontal } from 'lucide-react';
import UserAvatar from '../UserAvatar';
import useRingtone from '../../hooks/useRingtone';
import useCallRecording from '../../hooks/useCallRecording';
import './CallOverlay.css';

// Deploy marker — logged once on module load.
if (typeof window !== 'undefined' && !window.__CCUK_CALL_FIX_LOGGED__) {
  window.__CCUK_CALL_FIX_LOGGED__ = true;

  console.info('[CallOverlay] ccuk-call-fix=v7 (mobile-safe-viewport)');
}

function _ringingDots() {
  try {
    const tick = Math.floor(Date.now() / 700);
    const mod = ((tick % 4) + 4) % 4;
    return Math.max(0, mod);
  } catch {
    return 0;
  }
}

export default function CallOverlay({
  call,
  muted, cameraOff,
  localStream, remoteStream,
  acceptCall, declineCall, endCall,
  toggleMute, toggleCamera,
}) {
  // We attach streams via CALLBACK refs (not useRef + useEffect) so
  // we always (re-)attach when EITHER the element mounts OR the
  // stream reference changes — the legacy effect-only approach lost
  // the race in iter154 when remoteStream arrived before isAccepted.
  const recording = useCallRecording(call, localStream, remoteStream);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [miniPosition, setMiniPosition] = useState(null);
  const [keyboardInset, setKeyboardInset] = useState(0);
  const miniDrag = useRef(null);
  const stageRef = useRef(null);
  const moveMini = (event) => {
    const drag = miniDrag.current;
    if (!drag || event.pointerId !== drag.id) return;
    const viewport = window.visualViewport;
    const width = viewport?.width || window.innerWidth;
    const height = viewport?.height || window.innerHeight;
    const top = viewport?.offsetTop || 0;
    setMiniPosition({ x: Math.max(8, Math.min(event.clientX-drag.x, width-drag.width-8)),
      y: Math.max(top+8, Math.min(event.clientY-drag.y, top+height-drag.height-80)) });
  };
  const startMiniDrag = event => {
    const rect = stageRef.current.getBoundingClientRect();
    miniDrag.current = { id:event.pointerId, x:event.clientX-rect.left, y:event.clientY-rect.top, width:rect.width, height:rect.height };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const finishMiniDrag = event => {
    miniDrag.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };
  useEffect(() => {
    const resized = () => {
      const viewport = window.visualViewport;
      setKeyboardInset(viewport ? Math.max(0, window.innerHeight-viewport.height-viewport.offsetTop) : 0);
      setMiniPosition(null); setPipPosition(null);
    };
    window.visualViewport?.addEventListener('resize', resized);
    window.addEventListener('resize', resized);
    return () => { window.visualViewport?.removeEventListener('resize', resized); window.removeEventListener('resize', resized); };
  }, []);
  // Keep one portal container and the same media/control nodes when moving
  // the whole call between CCUK and a supported always-on-top call window.
  const [portalHost] = useState(() => {
    if (typeof document === 'undefined') return null;
    const host = document.createElement('div');
    host.setAttribute('data-ccuk-call-host', 'true');
    return host;
  });
  const popupRef = useRef(null);
  const openingPopupRef = useRef(false);
  const activeCallId = useRef(call?.id);
  activeCallId.current = call?.id;
  const [poppedOut, setPoppedOut] = useState(false);
  useLayoutEffect(() => {
    if (call?.id && portalHost) document.body.appendChild(portalHost);
    return () => {
      const popup = popupRef.current;
      popupRef.current = null;
      popup?.close();
      portalHost?.remove();
    };
  }, [call?.id, portalHost]);
  useEffect(() => { activeCallId.current = call?.id; return () => { activeCallId.current = null; }; }, [call?.id]);
  const [minimised, setMinimised] = useState(false);
  const [pipError, setPipError] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [framing, setFraming] = useState('landscape');
  const [fillVideo, setFillVideo] = useState(false);
  const localElRef = useRef(null);
  const remoteElRef = useRef(null);
  const pipElRef = useRef(null);
  // Whether the remote media element's autoplay was blocked by the
  // browser. We surface a single "Tap to enable sound" button when
  // this happens — the click is a user gesture that lets us retry
  // play() with no further restrictions.
  const [needsUnmute, setNeedsUnmute] = useState(false);
  // Remote video defaults to an uncropped fit inside the call window,
  // tapping the floating preview swaps local/remote, and the preview
  // can be dragged anywhere inside the safe viewport.
  const [primaryVideo, setPrimaryVideo] = useState('remote');
  const [pipPosition, setPipPosition] = useState(null);
  const dragRef = useRef(null);
  const suppressPipClickRef = useRef(false);

  const clampPipPosition = useCallback((x, y, width, height) => {
    const bounds = stageRef.current?.getBoundingClientRect();
    const viewport = typeof window !== 'undefined' ? window.visualViewport : null;
    const viewportLeft = bounds?.left ?? (viewport?.offsetLeft || 0);
    const viewportTop = bounds?.top ?? (viewport?.offsetTop || 0);
    const viewportWidth = bounds?.width || viewport?.width || window.innerWidth;
    const viewportHeight = bounds?.height || viewport?.height || window.innerHeight;
    const edgeGap = 8;
    const controlsReserve = 104;

    return {
      x: Math.max(
        viewportLeft + edgeGap,
        Math.min(x, viewportLeft + viewportWidth - width - edgeGap),
      ) - viewportLeft,
      // Keep the preview above the bottom call controls and inside the
      // *visible* Safari/Chrome viewport, not merely the layout viewport.
      y: Math.max(
        viewportTop + edgeGap,
        Math.min(y, viewportTop + viewportHeight - height - controlsReserve),
      ) - viewportTop,
    };
  }, []);

  const handlePipPointerDown = useCallback((event) => {
    const el = event.currentTarget;
    const rect = el.getBoundingClientRect();
    dragRef.current = {
      pointerId: event.pointerId,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      width: rect.width,
      height: rect.height,
      startX: event.clientX,
      startY: event.clientY,
      moved: false,
    };
    setPipPosition(clampPipPosition(rect.left, rect.top, rect.width, rect.height));
    try { el.setPointerCapture(event.pointerId); } catch { /* ignore */ }
  }, [clampPipPosition]);

  const handlePipPointerMove = useCallback((event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    if (
      Math.abs(event.clientX - drag.startX) > 4
      || Math.abs(event.clientY - drag.startY) > 4
    ) {
      drag.moved = true;
    }
    setPipPosition(clampPipPosition(
      event.clientX - drag.offsetX,
      event.clientY - drag.offsetY,
      drag.width,
      drag.height,
    ));
  }, [clampPipPosition]);

  const handlePipPointerUp = useCallback((event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    suppressPipClickRef.current = drag.moved;
    dragRef.current = null;
    try { event.currentTarget.releasePointerCapture(event.pointerId); } catch { /* ignore */ }
  }, []);

  const handlePipClick = useCallback(() => {
    if (suppressPipClickRef.current) {
      suppressPipClickRef.current = false;
      return;
    }
    setPrimaryVideo((current) => current === 'remote' ? 'local' : 'remote');
  }, []);

  useEffect(() => { if (recording.incoming) setMinimised(false); }, [recording.incoming]);

  // Reset the preferred layout for each new call.
  useEffect(() => {
    setPoppedOut(false);
    setOptionsOpen(false); setMiniPosition(null);
    setMinimised(false);
    setPipError('');
    setExpanded(false);
    setFraming(window.matchMedia?.('(max-width: 639px)').matches ? 'portrait' : 'landscape');
    setFillVideo(false);
    setPrimaryVideo('remote');
    setPipPosition(null);
  }, [call?.id]);

  // iOS Safari changes the visual viewport when toolbars collapse and
  // every phone/tablet changes dimensions on rotation. Re-clamp a dragged
  // picture-in-picture preview so it never becomes stranded off-screen.
  useEffect(() => {
    const keepPipVisible = () => {
      const el = pipElRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setPipPosition((current) => {
        if (!current) return current;
        const bounds = stageRef.current?.getBoundingClientRect();
        return clampPipPosition(current.x + (bounds?.left || 0), current.y + (bounds?.top || 0), rect.width, rect.height);
      });
    };

    const viewport = window.visualViewport;
    window.addEventListener('resize', keepPipVisible);
    window.addEventListener('orientationchange', keepPipVisible);
    viewport?.addEventListener('resize', keepPipVisible);
    viewport?.addEventListener('scroll', keepPipVisible);

    return () => {
      window.removeEventListener('resize', keepPipVisible);
      window.removeEventListener('orientationchange', keepPipVisible);
      viewport?.removeEventListener('resize', keepPipVisible);
      viewport?.removeEventListener('scroll', keepPipVisible);
    };
  }, [clampPipPosition, expanded, framing]);

  // WebAudio ringtone (no asset, no CDN). Runs from this hook for
  // both incoming + outgoing ringing phases; stops on every exit.
  useRingtone(call);

  // ── Stream attach helpers ─────────────────────────────────────
  const attachLocal = useCallback((el) => {
    localElRef.current = el;
    if (el && localStream && el.srcObject !== localStream) {
      el.srcObject = localStream;
      // Local preview is muted — never blocked by autoplay.
      const p = el.play();
      if (p && typeof p.catch === 'function') p.catch(() => { /* ignore */ });
    }
  }, [localStream]);

  const attachRemote = useCallback((el) => {
    remoteElRef.current = el;
    if (el && remoteStream && el.srcObject !== remoteStream) {
      el.srcObject = remoteStream;
      const p = el.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => {
          // Autoplay was blocked (typically iOS Safari without a
          // direct user gesture on this element). Surface the
          // tap-to-enable-sound button.
          setNeedsUnmute(true);
        });
      }
    }
  }, [remoteStream]);

  // Re-run attach when streams change after the elements were
  // already mounted (e.g. caller-side `ontrack` arriving after
  // the <video> mounted with no stream). Without this, the callback
  // ref only runs at mount.
  useEffect(() => { attachLocal(localElRef.current); }, [localStream, attachLocal]);
  useEffect(() => {
    attachRemote(remoteElRef.current);
    if (remoteStream) {
      // New stream — reset the unmute prompt; a re-attach will
      // re-trigger it if autoplay is still blocked.
      setNeedsUnmute(false);
    }
  }, [remoteStream, attachRemote]);

  const handleEnableSound = () => {
    const el = remoteElRef.current;
    if (!el) return;
    // .play() inside the click handler is a user gesture, so even
    // iOS Safari will honour it.
    const p = el.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => { /* keep button visible — user can tap again */ });
    } else {
      setNeedsUnmute(false);
    }
    setNeedsUnmute(false);
  };

  const minimiseCall = async () => {
    // Old video-only PiP may still be active after a browser update. Bring
    // that video back before showing the single in-page call widget.
    const id = call?.id;
    try {
      if (document.pictureInPictureElement) await document.exitPictureInPicture();
    } catch {
      setPipError('Close the separate video window, then minimise the call again.');
      return;
    }
    if (activeCallId.current !== id) return;
    popupRef.current?.close();
    setMinimised(true); setOptionsOpen(false); setPipPosition(null); setMiniPosition(null);
  };
  const enterPictureInPicture = async () => {
    setPipError('');
    if (!window.documentPictureInPicture?.requestWindow) {
      await minimiseCall(); // All controls stay with video on mobile.
      return;
    }
    if (openingPopupRef.current || popupRef.current) return;
    const id = call?.id;
    openingPopupRef.current = true;
    let popup;
    try {
      popup = await window.documentPictureInPicture.requestWindow({ width: 420, height: 540 });
      if (activeCallId.current !== id) { popup.close(); return; }
      // Document PiP supports arbitrary content, unlike video-only PiP.
      // Copy stylesheet rules, falling back to external links when unreadable.
      [...document.styleSheets].forEach(sheet => {
        try {
          const style = popup.document.createElement('style');
          style.textContent = [...sheet.cssRules].map(rule => rule.cssText).join('');
          popup.document.head.appendChild(style);
        } catch {
          if (!sheet.href) return;
          const link = popup.document.createElement('link');
          link.rel = 'stylesheet'; link.href = sheet.href;
          popup.document.head.appendChild(link);
        }
      });
      popup.document.title = 'CCUK call';
      popup.document.body.style.margin = '0';
      popup.document.body.appendChild(portalHost);
      popupRef.current = popup;
      setPoppedOut(true); setMinimised(false); setOptionsOpen(false); setPipPosition(null);
      popup.addEventListener('resize', () => setPipPosition(null));
      popup.addEventListener('pagehide', () => {
        if (popupRef.current !== popup) return;
        popupRef.current = null;
        if (activeCallId.current !== id) return;
        document.body.appendChild(portalHost);
        setPoppedOut(false); setMinimised(true); setPipPosition(null); setMiniPosition(null);
      }, { once: true });
    } catch {
      popup?.close();
      if (activeCallId.current === id) {
        document.body.appendChild(portalHost);
        setPoppedOut(false); setMinimised(true); setOptionsOpen(false);
        setPipError('The call is floating inside CCUK. Your browser could not open a separate call window.');
      }
    } finally { openingPopupRef.current = false; }
  };

  const savedRecording = recording.download && <div className="ccuk-recording-download" role="region" aria-label="Saved call recording">
    <strong>Recording ready</strong><span>Saved here until you download or dismiss. Download before leaving this page.</span>
    <a href={recording.download.url} download={recording.download.name}>Download recording</a>
    <button type="button" onClick={recording.dismiss}>Dismiss</button>
  </div>;
  if (!call || !portalHost) return savedRecording ? createPortal(savedRecording, document.body) : null;

  const isVideo = call.type === 'video';
  const isOutgoing = call.direction === 'outgoing';
  const isIncoming = call.direction === 'incoming';
  const isAccepted = call.status === 'accepted';
  const peerLabel = call.peerName || 'Casting Calls UK';
  const localIsPrimary = primaryVideo === 'local';

  return createPortal(
    <>
    <div
      data-call-minimised={minimised ? 'true' : 'false'}
      className={`ccuk-call-overlay ${minimised ? 'ccuk-call-mini fixed bottom-4 right-4 z-[100]' : 'fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm'}`}
      style={minimised ? (miniPosition ? {left:miniPosition.x,top:miniPosition.y,right:'auto',bottom:'auto'} : {'--ccuk-keyboard-inset':`${keyboardInset}px`}) : undefined}
      data-testid="call-overlay"
      role={minimised ? 'region' : 'dialog'}
      data-ccuk-fullscreen-overlay={minimised ? undefined : 'true'}
      aria-modal={minimised ? undefined : true}
      aria-label={`Call with ${peerLabel}`}
    >
      <div ref={stageRef} data-testid="call-window"
        className={`ccuk-call-window relative flex items-center justify-center overflow-hidden bg-slate-950 text-white shadow-2xl ${minimised ? 'h-72 w-full rounded-2xl' : expanded ? 'h-[100dvh] w-full' : 'h-[100dvh] w-full sm:h-[min(820px,92dvh)] sm:w-[min(1100px,94vw)] sm:rounded-2xl'}`}>
      <header className="ccuk-call-header" data-testid="call-layout-controls">
        <div className="ccuk-call-identity">
          {minimised && <button type="button" className="ccuk-call-drag" aria-label="Drag floating call" onPointerDown={startMiniDrag} onPointerMove={moveMini} onPointerUp={finishMiniDrag} onPointerCancel={finishMiniDrag}><GripHorizontal size={18}/></button>}
          <div><strong>{peerLabel}</strong><span role="status">{isAccepted ? ({connected:'Connected',disconnected:'Connection interrupted — reconnecting…',failed:'Connection failed',connecting:'Connecting…'}[call.connectionState] || 'Connecting…') : isIncoming ? 'Incoming call' : 'Calling…'}</span></div>
        </div>
        <div className="ccuk-call-header-actions">
          {isAccepted && <button type="button" aria-label={poppedOut ? 'Return call to CCUK' : minimised ? 'Restore call' : 'Minimise call and use chat'} title={poppedOut ? 'Return call to CCUK' : minimised ? 'Restore call' : 'Chat while calling'} onClick={() => {if (!minimised) {minimiseCall();return;}setMinimised(false);setOptionsOpen(false);setPipPosition(null);setMiniPosition(null);}}>{minimised ? <Maximize2 size={18}/> : <MessageSquare size={18}/>}</button>}
          {!minimised && <button type="button" aria-label="Call options" aria-expanded={optionsOpen} aria-controls="ccuk-call-options" onClick={()=>setOptionsOpen(v=>!v)}><MoreHorizontal size={22}/></button>}
        </div>
      </header>
      {!minimised && optionsOpen && <div id="ccuk-call-options" className="ccuk-call-options">
        <div className="ccuk-call-options-heading"><strong>Call options</strong><button type="button" aria-label="Close call options" onClick={()=>setOptionsOpen(false)}><X size={18}/></button></div>
        {isVideo && isAccepted && <>
          <label>Video frame<select aria-label="Video frame" value={framing} onChange={e=>{setFraming(e.target.value);setPipPosition(null);}}><option value="landscape">Landscape 16:9</option><option value="portrait">Portrait 9:16</option></select></label>
          <button type="button" aria-pressed={fillVideo} onClick={()=>setFillVideo(v=>!v)}>{fillVideo ? 'Fill (cropped)' : 'Fit (full picture)'}</button>
        </>}
        {isAccepted && !poppedOut && <button type="button" onClick={enterPictureInPicture}>{window.documentPictureInPicture?.requestWindow ? 'Pop out whole call' : 'Floating call'}</button>}
        <button type="button" aria-label={expanded ? 'Use windowed call' : 'Expand call'} onClick={()=>{setExpanded(v=>!v);setPipPosition(null);}}>{expanded ? 'Window' : 'Expand'}</button>
        <p>Framing changes the display, not your camera quality. Recordings save to this device and stop after 15 minutes.</p>
      </div>}
      {pipError && <div role="alert" className="ccuk-call-notice"><button type="button" onClick={()=>setPipError('')} aria-label="Dismiss call notice"><X size={16}/></button>{pipError}</div>}
      {recording.error && <div role="alert" className="ccuk-call-notice">{recording.error}</div>}
      {(recording.state === 'recording' || recording.peerRecording) && <div className="ccuk-recording-badge"><span/> {recording.peerRecording ? 'Participant recording' : 'Recording'}{recording.peerRecording && <button type="button" onClick={recording.revoke}>Stop permission</button>}</div>}
      {recording.incoming && <div className="ccuk-recording-consent" role="dialog" aria-label="Recording permission">
        <strong>Allow this call to be recorded?</strong><p>{peerLabel} wants to record both participants’ audio{isVideo ? ' and video' : ''} and download it to their device. You can withdraw permission during the recording.</p>
        <button type="button" onClick={()=>recording.respond(false)}>Decline recording</button><button type="button" onClick={()=>recording.respond(true)}>Allow recording</button>
      </div>}
      {/* Active video call. Either stream can fill the screen; tapping
          the floating preview swaps the two views. */}
      {isVideo && isAccepted && (
        <div className="ccuk-call-video-stage">
          <div data-testid="call-video-frame" style={{ aspectRatio: framing === 'portrait' ? '9 / 16' : '16 / 9', width: framing === 'portrait' ? 'auto' : '100%', height: framing === 'portrait' ? '100%' : 'auto', maxWidth: '100%', maxHeight: '100%', overflow: 'hidden', position: 'relative' }}>
          {localIsPrimary ? (
            <>
              <video
                disablePictureInPicture
                ref={attachLocal}
                autoPlay
                playsInline
                muted
                style={{ objectFit: fillVideo ? 'cover' : 'contain' }}
                className={`h-full w-full bg-black ${cameraOff ? 'opacity-0' : ''}`}
                data-testid="local-video-main"
              />
              {cameraOff && (
                <div className="absolute inset-0 flex items-center justify-center text-white/70">
                  Camera off
                </div>
              )}
            </>
          ) : (
            <video
                disablePictureInPicture
              ref={attachRemote}
              autoPlay
              playsInline
              className="h-full w-full bg-black"
              style={{ objectFit: fillVideo ? 'cover' : 'contain' }}
              data-testid="remote-video"
            />
          )}
          </div>
        </div>
      )}
      {/* Voice call — remote audio element (hidden, no controls). */}
      {!isVideo && (isAccepted || remoteStream) && (
        <audio
          ref={attachRemote}
          autoPlay
          data-testid="remote-audio"
          style={{ display: 'none' }}
        />
      )}

      {/* Peer card — shown for ringing OR for voice-call accepted. */}
      {!(isAccepted && isVideo) && (
        <div className="relative z-10 flex flex-col items-center gap-4 px-6 text-center text-white">
          <UserAvatar
            user={{ id: call.peerId, name: peerLabel, profile_image: call.peerAvatar }}
            size="2xl"
            className="h-32 w-32 ring-4 ring-white/20"
          />
          <div>
            <div className="text-xl font-semibold" data-testid="call-peer-name">{peerLabel}</div>
            <div className="text-sm text-white/70 mt-1" data-testid="call-status">
              {isOutgoing && call.status === 'ringing' && `Calling${'.'.repeat(_ringingDots())}`}
              {isIncoming && call.status === 'ringing' && (isVideo ? 'Incoming video call' : 'Incoming voice call')}
              {isAccepted && !isVideo && 'Voice call'}
            </div>
          </div>
        </div>
      )}

      {/* Before connection, show the usual local preview. */}
      {isVideo && !isAccepted && localStream && (
        <div className="absolute top-20 right-4 z-20 h-32 w-24 sm:h-40 sm:w-28 rounded-xl overflow-hidden border-2 border-white/30 shadow-xl">
          <video
                disablePictureInPicture
            ref={attachLocal}
            autoPlay
            playsInline
            muted
            className={`h-full w-full object-cover bg-black ${cameraOff ? 'opacity-0' : ''}`}
            data-testid="local-video"
          />
          {cameraOff && (
            <div className="absolute inset-0 flex items-center justify-center bg-black text-white/70 text-xs">
              Camera off
            </div>
          )}
        </div>
      )}

      {/* Connected-call floating preview. Drag anywhere; tap to swap
          who fills the screen. touchAction:none prevents mobile page
          gestures stealing the drag. */}
      {isVideo && isAccepted && (
        <div
          ref={pipElRef}
          className={`ccuk-call-self ${minimised ? 'hidden' : ''} absolute z-30 h-32 w-24 sm:h-40 sm:w-28 rounded-xl overflow-hidden border-2 border-white/40 shadow-2xl bg-black cursor-grab active:cursor-grabbing select-none ${pipPosition ? '' : 'top-20 right-4'}`}
          style={pipPosition ? {
            left: `${pipPosition.x}px`,
            top: `${pipPosition.y}px`,
            touchAction: 'none',
          } : { touchAction: 'none' }}
          onPointerDown={handlePipPointerDown}
          onPointerMove={handlePipPointerMove}
          onPointerUp={handlePipPointerUp}
          onPointerCancel={handlePipPointerUp}
          onClick={handlePipClick}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') handlePipClick();
          }}
          aria-label={localIsPrimary ? 'Show the other participant full screen' : 'Show my video full screen'}
          title="Drag to move · tap to swap"
          data-testid="call-floating-video"
        >
          {localIsPrimary ? (
            <video
                disablePictureInPicture
              ref={attachRemote}
              autoPlay
              playsInline
              className="h-full w-full object-cover bg-black pointer-events-none"
              data-testid="remote-video-preview"
            />
          ) : (
            <>
              <video
                disablePictureInPicture
                ref={attachLocal}
                autoPlay
                playsInline
                muted
                className={`h-full w-full object-cover bg-black pointer-events-none ${cameraOff ? 'opacity-0' : ''}`}
                data-testid="local-video"
              />
              {cameraOff && (
                <div className="absolute inset-0 flex items-center justify-center bg-black text-white/70 text-xs pointer-events-none">
                  Camera off
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Autoplay-blocked → "Tap to enable sound". Surfaces when the
          remote element's .play() rejects (typically iOS Safari).
          A single tap counts as a user gesture and resumes audio. */}
      {needsUnmute && isAccepted && (
        <button
          type="button"
          onClick={handleEnableSound}
          className="absolute top-20 left-4 z-30 inline-flex items-center gap-2 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold px-4 py-2 shadow-lg active:scale-95 transition-all"
          data-testid="call-enable-sound-btn"
          aria-label="Tap to enable sound"
        >
          <Volume2 className="h-4 w-4" />
          Tap to enable sound
        </button>
      )}

      {/* Controls — depend on direction + status */}
      <div
        className="ccuk-call-footer"
        data-testid="call-controls"
      >
        {isIncoming && call.status === 'ringing' && (
          <>
            <button
              type="button"
              onClick={declineCall}
              className="h-16 w-16 rounded-full bg-rose-600 hover:bg-rose-700 text-white flex items-center justify-center shadow-lg active:scale-95 transition-transform"
              aria-label="Decline call"
              data-testid="call-decline-btn"
            >
              <PhoneOff className="h-7 w-7" />
            </button>
            {isVideo && (
              <button
                type="button"
                onClick={() => acceptCall('voice')}
                className="h-16 w-16 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center shadow-lg active:scale-95 transition-transform"
                aria-label="Accept as voice"
                data-testid="call-accept-voice-btn"
                title="Accept as voice only"
              >
                <Phone className="h-7 w-7" />
              </button>
            )}
            <button
              type="button"
              onClick={() => acceptCall()}
              className="h-16 w-16 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white flex items-center justify-center shadow-lg active:scale-95 transition-transform"
              aria-label={isVideo ? 'Accept video call' : 'Accept voice call'}
              data-testid="call-accept-btn"
            >
              {isVideo ? <Video className="h-7 w-7" /> : <Phone className="h-7 w-7" />}
            </button>
          </>
        )}

        {(isOutgoing && call.status === 'ringing') && (
          <button
            type="button"
            onClick={endCall}
            className="h-16 w-16 rounded-full bg-rose-600 hover:bg-rose-700 text-white flex items-center justify-center shadow-lg active:scale-95 transition-transform"
            aria-label="Cancel call"
            data-testid="call-cancel-btn"
          >
            <PhoneOff className="h-7 w-7" />
          </button>
        )}

        {isAccepted && (
          <>
            <button
              type="button"
              onClick={toggleMute}
              className={`h-14 w-14 rounded-full ${muted ? 'bg-white/25' : 'bg-white/10'} hover:bg-white/30 text-white flex items-center justify-center shadow-lg active:scale-95 transition-transform`}
              aria-label={muted ? 'Unmute' : 'Mute'}
              data-testid="call-mute-btn"
            >
              {muted ? <MicOff className="h-6 w-6" /> : <Mic className="h-6 w-6" />}
            </button>
            {isVideo && (
              <button
                type="button"
                onClick={toggleCamera}
                className={`h-14 w-14 rounded-full ${cameraOff ? 'bg-white/25' : 'bg-white/10'} hover:bg-white/30 text-white flex items-center justify-center shadow-lg active:scale-95 transition-transform`}
                aria-label={cameraOff ? 'Turn camera on' : 'Turn camera off'}
                data-testid="call-camera-btn"
              >
                {cameraOff ? <VideoOff className="h-6 w-6" /> : <Video className="h-6 w-6" />}
              </button>
            )}
            {(!minimised || recording.state !== 'idle') && <button type="button" className="ccuk-call-record" aria-label={recording.state === 'recording' ? 'Stop recording' : recording.state === 'waiting' ? 'Cancel recording request' : 'Request call recording'} disabled={recording.peerRecording || (call.connectionState !== 'connected' && recording.state === 'idle')} onClick={recording.state === 'idle' ? recording.request : recording.stop}>
              {recording.state === 'recording' ? <Square size={20}/> : <Circle size={20}/>}<span>{recording.state === 'recording' ? 'Stop' : recording.state === 'waiting' ? 'Cancel request' : 'Record'}</span>
            </button>}
            <button
              type="button"
              onClick={endCall}
              className="h-16 w-16 rounded-full bg-rose-600 hover:bg-rose-700 text-white flex items-center justify-center shadow-lg active:scale-95 transition-transform"
              aria-label="End call"
              data-testid="call-end-btn"
            >
              <PhoneOff className="h-7 w-7" />
            </button>
          </>
        )}
      </div>
      </div>
    </div>
    {savedRecording && createPortal(savedRecording, popupRef.current?.document.body || document.body)}
    </>,
    portalHost,
  );
}

